import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { useAppSelector, selectIsAuthenticated, selectCurrentUser } from '@nimbazaar/store';
import type { MessageWithSender } from '@nimbazaar/types';
import { useToast } from './Toast';

// Create notification sound using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 830;
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0.3, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.15);

    // Second tone (higher)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1050;
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc2.start(ctx.currentTime + 0.12);
    osc2.stop(ctx.currentTime + 0.35);

    osc2.onended = () => ctx.close();
  } catch {
    // Audio not available
  }
}

export function ChatNotificationProvider() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  const { socket } = useSocket();
  const location = useLocation();
  const toast = useToast();

  // Use refs to avoid re-registering the listener on every render
  const locationRef = useRef(location.pathname);
  const currentUserIdRef = useRef(currentUser?.id);
  const toastRef = useRef(toast);
  const notifiedIds = useRef(new Set<string>());

  useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { currentUserIdRef.current = currentUser?.id; }, [currentUser?.id]);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const handler = (message: MessageWithSender) => {
      const userId = currentUserIdRef.current;

      // Don't notify for own messages
      if (!userId || message.senderId === userId) return;

      // Deduplicate (message may arrive via both conversation room and user room)
      if (notifiedIds.current.has(message.id)) return;
      notifiedIds.current.add(message.id);
      if (notifiedIds.current.size > 100) {
        const ids = Array.from(notifiedIds.current);
        notifiedIds.current = new Set(ids.slice(-50));
      }

      // Don't notify if user is currently viewing this conversation
      if (locationRef.current === `/inbox/${message.conversationId}`) return;

      // Play sound
      playNotificationSound();

      // Show toast notification
      const senderName = message.sender?.name || 'Someone';
      const preview = message.type === 'IMAGE'
        ? 'Sent an image'
        : (message.content?.length > 50
            ? message.content.slice(0, 50) + '...'
            : message.content || 'New message');

      toastRef.current.info(senderName, preview);
    };

    socket.on('new_message', handler);

    return () => {
      socket.off('new_message', handler);
    };
  }, [socket, isAuthenticated]);

  return null;
}
